/**
 * NotificationsPage - the full-page notification centre at `/notifications`.
 *
 * The bell opens `NotificationsSidepanel`, which is a peek: two fixed-height
 * bands, no filters, no paging. This is the place you go when the peek is not
 * enough - the whole pile, filterable, searchable, paged.
 *
 * It reads the SAME redux slice the sidepanel and the dashboard widget read
 * (`state.notifications`), which is already synced app-wide by
 * `useNotificationSync` + the PubSub `notifications` hub, so a mark-read here
 * drops the bell's badge without a second request. The mount-time
 * `GetNotifications()` is a refresh, not the source of truth.
 *
 * THE SCROLL CONTRACT is the list-section one (`listSectionLayout`): the page
 * does not scroll, the toolbar band is fixed, and the row list inside the glass
 * surface is the only scroller. That is what keeps "Mark all as read" and the
 * pager on screen with a hundred rows behind them.
 *
 * PAGING IS CLIENT-SIDE on purpose. `GET /notifications` caps at 100 rows and
 * the slice holds the same array the bell counts from; splitting the page onto
 * a server cursor would give the bell a different list than the page. A hundred
 * rows page fine in memory.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiBellLight,
  PiBellSimpleLight,
  PiBriefcaseLight,
  PiBuildingLight,
  PiCheckLight,
  PiCheckSquareOffsetLight,
  PiUsersThreeLight,
} from 'react-icons/pi';

import { RootState, useAppDispatch, useAppSelector } from '../../store';
import { LayoutContext } from '../../contexts/LayoutContext';
import {
  GetNotifications,
  markAllNotificationsAsRead,
  updateNotification,
} from '../../store/actions/Notifications';
import { AppNotification } from './notificationSlice';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs, { TabItem } from '../shared/TailwindComponents/FilterBarWithTabs';
import GlassCard from '../shared/brand/GlassCard';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import TablePagination, { PAGE_SIZE_OPTIONS } from '../shared/TailwindComponents/TablePagination';
import ProfileImage from '../Header/ProfileImage';
import { formatRelativeTime, formatAbsoluteTime } from '../shared/feed/feedTime';
import {
  LIST_SECTION_CHROME,
  LIST_SECTION_SHELL,
  LIST_SECTION_SURFACE,
} from '../Settings/framework/listSectionLayout';

/* ------------------------------------------------------------------ */
/*  How a notification type reads                                      */
/* ------------------------------------------------------------------ */

/**
 * Glyph + tint per notification type.
 *
 * The tint is a semantic status ramp, never a raw colour, and it is only ever
 * applied to a 32px disc - a list where every row carries a filled colour band
 * is a wall, not a signal. Unknown types fall through to the neutral bell,
 * which is why the backend adding a type can never break this page.
 */
interface TypePresentation {
  icon: React.ReactNode;
  /** Disc classes - a soft status wash with a matching glyph colour. */
  tint: string;
  /** Already-translatable English label for the type filter and the badge. */
  label: string;
}

const TYPE_PRESENTATION: Record<string, TypePresentation> = {
  task_assigned: {
    icon: <PiCheckSquareOffsetLight />,
    tint: 'bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-300',
    label: 'Tasks',
  },
  collaborator_added: {
    icon: <PiUsersThreeLight />,
    tint: 'bg-success-50 text-success-600 dark:bg-success-900/25 dark:text-success-300',
    label: 'Collaboration',
  },
  matter_updated: {
    icon: <PiBriefcaseLight />,
    tint: 'bg-warning-50 text-warning-600 dark:bg-warning-900/25 dark:text-warning-300',
    label: 'Matters',
  },
  draft_completed: {
    icon: <PiBriefcaseLight />,
    tint: 'bg-warning-50 text-warning-600 dark:bg-warning-900/25 dark:text-warning-300',
    label: 'Drafts',
  },
  team_invite: {
    icon: <PiUsersThreeLight />,
    tint: 'bg-success-50 text-success-600 dark:bg-success-900/25 dark:text-success-300',
    label: 'Teams',
  },
  team_feed_post: {
    icon: <PiUsersThreeLight />,
    tint: 'bg-success-50 text-success-600 dark:bg-success-900/25 dark:text-success-300',
    label: 'Team feed',
  },
  documents_uploaded: {
    icon: <PiBuildingLight />,
    tint: 'bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-300',
    label: 'Documents',
  },
};

const DEFAULT_PRESENTATION: TypePresentation = {
  icon: <PiBellLight />,
  tint: 'bg-surface-sunken text-fg-muted',
  label: 'Other',
};

const presentationFor = (type?: string): TypePresentation =>
  (type && TYPE_PRESENTATION[type]) || DEFAULT_PRESENTATION;

/** "matter_updated" → "Matter updated", for a type the map does not know. */
const humanizeType = (type?: string): string => {
  if (!type) return '';
  const spaced = type.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/* ------------------------------------------------------------------ */
/*  Actor                                                              */
/* ------------------------------------------------------------------ */

/**
 * The person a notification is ABOUT, when the payload names one.
 *
 * `GET /notifications` does not currently emit actor identity - it serializes
 * `notification_id / user_id / title / content / type / link / is_read /
 * created_at` and nothing else. Rather than hard-code that gap, the row reads
 * the fields defensively: the moment the backend adds any of them the avatars
 * appear with no frontend change, and until then every row falls back to its
 * type glyph. Reading through `any` is deliberate - typing fields the API does
 * not send yet onto `AppNotification` would claim they exist.
 */
interface NotificationActor {
  name: string;
  email: string | null;
  picture: string | null;
}

const actorOf = (notification: AppNotification): NotificationActor | null => {
  const raw = notification as any;
  const meta = raw.metadata || raw.data || {};
  const first = raw.actor_first_name ?? meta.actor_first_name;
  const last = raw.actor_last_name ?? meta.actor_last_name;
  const name =
    raw.actor_name ??
    meta.actor_name ??
    raw.sender_name ??
    meta.sender_name ??
    [first, last].filter(Boolean).join(' ');

  if (!name || !String(name).trim()) return null;

  return {
    name: String(name).trim(),
    email: raw.actor_email ?? meta.actor_email ?? raw.sender_email ?? meta.sender_email ?? null,
    picture:
      raw.actor_profile_picture ??
      meta.actor_profile_picture ??
      raw.sender_profile_picture ??
      meta.sender_profile_picture ??
      null,
  };
};

/** "Ada Lovelace" → "AL". Two letters at most; a wall of three reads as noise. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/* ------------------------------------------------------------------ */
/*  One row                                                            */
/* ------------------------------------------------------------------ */

interface NotificationRowProps {
  notification: AppNotification;
  onOpen: (notification: AppNotification) => void;
  onMarkRead: (notification: AppNotification) => void;
}

/**
 * A row is a glass card, and read/unread is carried by WEIGHT, not by a colour
 * fill: an unread row keeps a champagne edge, a champagne dot and full-strength
 * text; a read one loses the dot, drops to a soft hairline and mutes its title.
 * The old panel painted every unread row `bg-info-50` with an `info` border,
 * which turned a full inbox into a solid blue block.
 */
const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  onOpen,
  onMarkRead,
}) => {
  const { t } = useTranslation();
  const presentation = presentationFor(notification.type);
  const actor = actorOf(notification);
  const unread = !notification.is_read;

  const title = notification.title || notification.content;
  // The backend derives `title` from the type, so it is often a restatement of
  // the body. Only show the body when it actually says something more.
  const body = notification.title && notification.content !== notification.title
    ? notification.content
    : '';

  return (
    <li>
      <GlassCard
        padding="none"
        className={clsx(
          'transition-shadow duration-200 ease-slide hover:shadow-md',
          unread ? '!border-champagne/40' : 'opacity-85 hover:opacity-100'
        )}
      >
        <div
          role="button"
          tabIndex={0}
          aria-label={title}
          onClick={() => onOpen(notification)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onOpen(notification);
            }
          }}
          className="flex w-full cursor-pointer items-start gap-3 rounded-lg p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
        >
          {/* Identity first when we have one - a face is a faster read than a
              glyph. The glyph then rides the avatar as a small type badge so
              the category is never lost. */}
          <span className="relative flex-shrink-0">
            {actor ? (
              <>
                <ProfileImage
                  profilePicture={actor.picture}
                  email={actor.email}
                  initials={initialsOf(actor.name)}
                  name={actor.name}
                  size="h-9 w-9"
                />
                <span
                  aria-hidden
                  className={clsx(
                    'absolute -bottom-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-hairline',
                    presentation.tint,
                    '[&>svg]:h-2.5 [&>svg]:w-2.5'
                  )}
                >
                  {presentation.icon}
                </span>
              </>
            ) : (
              <span
                aria-hidden
                className={clsx(
                  'flex h-9 w-9 items-center justify-center rounded-full',
                  presentation.tint,
                  '[&>svg]:h-4 [&>svg]:w-4'
                )}
              >
                {presentation.icon}
              </span>
            )}
          </span>

          <span className="min-w-0 flex-1">
            <span className="flex items-start justify-between gap-3">
              <span
                className={clsx(
                  'min-w-0 text-sm leading-snug',
                  unread ? 'font-medium text-fg-strong' : 'text-fg-body'
                )}
              >
                {title}
              </span>
              <span
                className="flex-shrink-0 whitespace-nowrap text-[11px] text-fg-muted"
                title={formatAbsoluteTime(notification.created_at)}
              >
                {formatRelativeTime(notification.created_at, t)}
              </span>
            </span>

            {body && (
              <span className="mt-1 block line-clamp-2 text-xs text-fg-muted">{body}</span>
            )}

            <span className="mt-1.5 flex items-center gap-2 text-[11px] text-fg-muted">
              {unread && (
                <span className="inline-flex items-center gap-1.5 text-fg-body">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-champagne" />
                  {t('Unread')}
                </span>
              )}
              {actor && <span className="truncate">{actor.name}</span>}
              {notification.type && (
                <span className="truncate">{t(humanizeType(notification.type))}</span>
              )}
            </span>
          </span>

          {/* Marking read without leaving the page - the row's click navigates
              away, so catching up on a pile needs its own control. */}
          {unread && (
            <button
              type="button"
              title={t('Mark as read')}
              aria-label={t('Mark as read')}
              onClick={(event) => {
                event.stopPropagation();
                onMarkRead(notification);
              }}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-hairline text-fg-muted transition-colors hover:bg-surface-sunken hover:text-fg-body focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
            >
              <PiCheckLight className="h-4 w-4" />
            </button>
          )}
        </div>
      </GlassCard>
    </li>
  );
};

/* ------------------------------------------------------------------ */
/*  The page                                                           */
/* ------------------------------------------------------------------ */

type ReadTab = 'all' | 'unread' | 'read';

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);

  const notifications = useAppSelector(
    (state: RootState) => state.notifications.notifications
  ) as AppNotification[];
  const unreadCount = useAppSelector((state: RootState) => state.notifications.unreadCount);

  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState<ReadTab>('all');
  const [search, setSearch] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  // The list owns the only scroller (see the file header), so the page scroller
  // goes off for as long as this page is mounted.
  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await dispatch(GetNotifications());
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  /** Every type present in the pile, so the filter never offers an empty one. */
  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    notifications.forEach((notification) => {
      const type = notification.type;
      if (!type || seen.has(type)) return;
      const known = TYPE_PRESENTATION[type];
      seen.set(type, t(known ? known.label : humanizeType(type)));
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [notifications, t]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return notifications
      .filter((notification) => {
        if (tab === 'unread' && notification.is_read) return false;
        if (tab === 'read' && !notification.is_read) return false;
        if (selectedTypes.length && !selectedTypes.includes(notification.type || '')) return false;
        if (!query) return true;
        return `${notification.title || ''} ${notification.content || ''}`
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }, [notifications, tab, selectedTypes, search]);

  const totalPages = Math.max(Math.ceil(filtered.length / pageSize), 1);
  // A filter change can strand the viewer past the end of the new result set.
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [tab, search, selectedTypes, pageSize]);

  const openNotification = useCallback(
    async (notification: AppNotification) => {
      if (!notification.is_read) {
        try {
          await dispatch(updateNotification(notification.notification_id) as any);
        } catch {
          // The reducer is the source of truth for the badge; a failed
          // mark-read must still not swallow the navigation.
        }
      }
      if (notification.link) history.push(notification.link);
    },
    [dispatch, history]
  );

  const markRead = useCallback(
    async (notification: AppNotification) => {
      try {
        await dispatch(updateNotification(notification.notification_id) as any);
      } catch {
        /* handled by the action's error reporting */
      }
    },
    [dispatch]
  );

  const markAllRead = useCallback(async () => {
    await dispatch(markAllNotificationsAsRead() as any);
  }, [dispatch]);

  const tabs: TabItem[] = [
    { label: t('All'), value: 'all' },
    { label: t('Unread'), value: 'unread' },
    { label: t('Read'), value: 'read' },
  ];

  const emptyHeadline =
    tab === 'unread'
      ? t('Nothing unread')
      : search || selectedTypes.length
      ? t('No notifications match your filters')
      : t('No notifications yet');

  const emptySubtext =
    tab === 'unread'
      ? t('You are all caught up.')
      : search || selectedTypes.length
      ? t('Try a different search or clear the filters.')
      : t('Activity on your matters, tasks and teams will show up here.');

  return (
    <div className={LIST_SECTION_SHELL}>
      <div className={LIST_SECTION_CHROME}>
        <Header
          actions={
            unreadCount > 0
              ? [
                  {
                    label: t('Mark all as read'),
                    onClick: markAllRead,
                    icon: <PiCheckLight />,
                    variant: 'outline' as const,
                  },
                ]
              : []
          }
        />

        <FilterBarWithTabs
          tabs={tabs}
          activeTab={tab}
          onTabClick={(value) => setTab(value as ReadTab)}
          onSearchChange={setSearch}
          filters={
            typeOptions.length
              ? [{ id: 'type', name: t('Type'), options: typeOptions }]
              : []
          }
          selectedFilters={{ type: selectedTypes }}
          onFilterChange={(_name, values) => setSelectedTypes(values)}
          onClearAllFilters={() => setSelectedTypes([])}
          itemCount={filtered.length}
          totalItemCount={notifications.length}
          itemCountLabel={t('notifications')}
        />
      </div>

      {/* `bodyClassName` is load-bearing: GlassCard always wraps its children in
          a body div, and an unstyled one is content-sized - the row list's
          `flex-1 min-h-0` would then resolve against `auto`, the card would grow
          to all its rows, and nothing would scroll. */}
      <GlassCard
        padding="none"
        className={LIST_SECTION_SURFACE}
        bodyClassName="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar-main p-3">
          {isLoading && notifications.length === 0 ? (
            <Skeleton variant="list" rows={8} />
          ) : pageRows.length === 0 ? (
            <EmptyState
              icon={<PiBellSimpleLight />}
              headline={emptyHeadline}
              subtext={emptySubtext}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {pageRows.map((notification) => (
                <NotificationRow
                  key={notification.notification_id}
                  notification={notification}
                  onOpen={openNotification}
                  onMarkRead={markRead}
                />
              ))}
            </ul>
          )}
        </div>

        {filtered.length > pageSize && (
          <div className="shrink-0 border-t border-hairline px-3 py-2">
            <TablePagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default NotificationsPage;
